import type React from 'react';

import { AdornedInput, Box, FormField, Label, Paragraph, Tooltip } from '@zillow/constellation-10';

import type { Section } from '../../..//models';
import { TENANT_DEFAULT } from '../../../constants';
import { stopPropagationIfInvalidNumber } from '../../../utils/validatorTypes/PositiveIntegerValidator';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function TenantDefault_v4({ section, setSectionValues }: Props) {
  function onDaysAfterUnpaidChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_UNPAID]: value,
    });
  }

  function onDaysAfterReceivingNoticeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_RECEIVING_NOTICE]: value,
    });
  }

  return (
    <>
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter', marginBottom: 'layout.tighter' }}>
        <Paragraph textStyle="body-lg-bold">
          How long is the grace period before the tenant defaults on the lease?
        </Paragraph>
        <Tooltip content="A default occurs when, after receiving written notice and an opportunity to cure any breach of the lease, a tenant fails to cure the breach within the grace period provided in the lease." />
      </Box>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={
          <Label>
            Number of days after receiving written notice from landlord of failure to pay monthly or additional rent
          </Label>
        }
        control={
          <AdornedInput.Root>
            <AdornedInput.Input
              type="number"
              onChange={onDaysAfterUnpaidChange}
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_UNPAID)}
            />
            <AdornedInput.Adornment>days</AdornedInput.Adornment>
          </AdornedInput.Root>
        }
      />
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={
          <Label>
            Number of days after receiving written notice from landlord of failure to live up to other obligations
          </Label>
        }
        control={
          <AdornedInput.Root>
            <AdornedInput.Input
              type="number"
              onChange={onDaysAfterReceivingNoticeChange}
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_RECEIVING_NOTICE)}
            />
            <AdornedInput.Adornment>days</AdornedInput.Adornment>
          </AdornedInput.Root>
        }
      />
    </>
  );
}

TenantDefault_v4.description = 'Asks between days since failure and since receiving notice';

export default TenantDefault_v4;
